// src/redux/slices/togglesSlice.js

import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data ||
  error?.message ||
  fallback;

const handleAuthError = async (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch {
      // ничего не делаем
    }
  }
};

/**
 * GET /feature-toggles/:uid
 */
export const fetchToggles = createAsyncThunk(
  "toggles/fetchToggles",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) {
        throw new Error("UID is required");
      }

      const response = await apiRequest(
        "GET",
        `/feature-toggles/${uid}`
      );

      return response?.data || response || {};
    } catch (error) {
      /*
       * Отсутствующая запись с toggles — не ошибка.
       * В этом случае баннеры остаются видимыми.
       */
      if (error?.response?.status === 404) {
        return {};
      }

      await handleAuthError(error);

      console.error(
        "[fetchToggles error]",
        error?.response?.status,
        error?.response?.data || error
      );

      return rejectWithValue(
        getErrorMessage(
          error,
          "Failed to fetch toggles"
        )
      );
    }
  }
);

/**
 * POST /feature-toggles
 *
 * Payload:
 * {
 *   uid,
 *   changes
 * }
 */
export const setToggles = createAsyncThunk(
  "toggles/setToggles",
  async (
    {
      uid,
      changes,
    },
    {
      rejectWithValue,
    }
  ) => {
    try {
      if (!uid) {
        throw new Error("UID is required");
      }

      if (
        !changes ||
        typeof changes !== "object" ||
        Array.isArray(changes)
      ) {
        throw new Error("Changes object is required");
      }

      const response = await apiRequest(
        "POST",
        "/feature-toggles",
        {
          uid,
          changes,
        }
      );

      const data = response?.data || response || {};

      return data?.flags || data || {};
    } catch (error) {
      await handleAuthError(error);

      console.error(
        "[setToggles error]",
        error?.response?.status,
        error?.response?.data || error
      );

      return rejectWithValue(
        getErrorMessage(
          error,
          "Failed to set toggles"
        )
      );
    }
  }
);

/**
 * PATCH /feature-toggles/:uid/:key
 *
 * Payload:
 * {
 *   value
 * }
 */
export const setToggle = createAsyncThunk(
  "toggles/setToggle",
  async (
    {
      uid,
      key,
      value,
    },
    {
      rejectWithValue,
    }
  ) => {
    try {
      if (!uid) {
        throw new Error("UID is required");
      }

      if (!key) {
        throw new Error("Toggle key is required");
      }

      const response = await apiRequest(
        "PATCH",
        `/feature-toggles/${uid}/${encodeURIComponent(key)}`,
        {
          value,
        }
      );

      const data = response?.data || response || {};

      return data?.flags || data || {};
    } catch (error) {
      await handleAuthError(error);

      console.error(
        "[setToggle error]",
        error?.response?.status,
        error?.response?.data || error
      );

      return rejectWithValue(
        getErrorMessage(
          error,
          "Failed to set toggle"
        )
      );
    }
  }
);

const initialState = {
  flags: {},
  status: "idle",
  error: null,
};

const togglesSlice = createSlice({
  name: "toggles",

  initialState,

  reducers: {
    clearTogglesError: (state) => {
      state.error = null;
    },

    resetTogglesStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },

    clearToggles: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchToggles.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })

      .addCase(fetchToggles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;

        state.flags =
          action.payload &&
          typeof action.payload === "object" &&
          !Array.isArray(action.payload)
            ? action.payload
            : {};
      })

      .addCase(fetchToggles.rejected, (state, action) => {
        state.status = "failed";

        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch toggles failed";

        /*
         * Не записываем undefined.
         */
        state.flags = {};
      })

      // SET MANY
      .addCase(setToggles.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })

      .addCase(setToggles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;

        state.flags =
          action.payload &&
          typeof action.payload === "object" &&
          !Array.isArray(action.payload)
            ? action.payload
            : {};
      })

      .addCase(setToggles.rejected, (state, action) => {
        state.status = "failed";

        state.error =
          action.payload ||
          action.error?.message ||
          "Set toggles failed";
      })

      // SET ONE
      .addCase(setToggle.pending, (state, action) => {
        state.status = "loading";
        state.error = null;

        const {
          key,
          value,
        } = action.meta?.arg || {};

        if (key) {
          state.flags[key] = value;
        }
      })

      .addCase(setToggle.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;

        state.flags =
          action.payload &&
          typeof action.payload === "object" &&
          !Array.isArray(action.payload)
            ? action.payload
            : {};
      })

      .addCase(setToggle.rejected, (state, action) => {
        state.status = "failed";

        state.error =
          action.payload ||
          action.error?.message ||
          "Set toggle failed";

        /*
         * Откатываем оптимистическое изменение.
         */
        const {
          key,
          value,
        } = action.meta?.arg || {};

        if (key) {
          state.flags[key] = !value;
        }
      })

      // RESET
      .addCase(resetStore, () => initialState);
  },
});

export const {
  clearTogglesError,
  resetTogglesStatus,
  clearToggles,
} = togglesSlice.actions;

export default togglesSlice.reducer;